import { navigate } from "gatsby";
import { getImage } from "gatsby-plugin-image";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import InfoIcon from "images/icons/info-outline.inline.svg";
import Tick from "images/icons/tick-white.inline.svg";

import { useTheme } from "../../context/ThemeContext";
import { arrayRange, sortBy } from "../../utils/commonFunctions";
import { flattenMenu } from "../../utils/flattenMenu";
import { formatCurrencyAmount } from "../../utils/formatContent";
import { processForm } from "../../utils/processForm";
import {
  formDropdownPrimaryStyle,
  step2_exclusion_list
} from "../../utils/sharedDatasets";
import Image from "../image";
import { FormInputSelect } from "../shared/form/FormInputSelect";
import { StatusComp } from "../shared/statusComp";

const RequestForm = ({
  currentUrl,
  programs,
  backImage,
  calendlyActive = true,
  programInterests,
  budgetPricing
}) => {
  const {
    peopleDomain,
    planetDomain,
    currentBrand,
    localeLanguage,
    currentSiteMeta
  } = useTheme();

  const intlNumber = currentSiteMeta?.contact_details?.international ?? "";

  const [programTitle, setProgramTitle] = useState("");
  const [checked, setChecked] = useState(true);
  const [programCode, setProgramCode] = useState("");
  const [programLegacyId, setProgramLegacyId] = useState("");
  const [programRecordTypeId, setProgramRecordTypeId] = useState("");
  const [type, setType] = useState("enquiry");
  const [status, setStatus] = useState("pending");
  const [tooltipOpen, setTooltipOpen] = useState(0);
  const [calendlyPrefill, setCalendlyPrefill] = useState({});
  const [eventId, setEventId] = useState("");

  const [speakToConsultant, setSpeakToConsultant] = useState(calendlyActive);

  const backLocalImg = backImage ? getImage(backImage?.localFile) : false;
  const mediaItemUrl = backImage.mediaItemUrl ?? backImage;
  let showPromo = true;

  const formOptions = {
    defaultValues: {
      bookings: "",
      travel_next_year: ""
    }
  };

  const form = useForm(formOptions);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue
  } = form;

  useEffect(() => {
    let url = new URL(currentUrl);
    setProgramTitle(url.searchParams.get("program_title"));
    setProgramCode(url.searchParams.get("program_code"));
    setProgramLegacyId(
      url.searchParams.get("program") ?? url.searchParams.get("program_id")
    );
    setProgramRecordTypeId(url.searchParams.get("record_type_id"));
    setEventId(url.searchParams.get("event_id"));
    let formtype = url.searchParams.get("type") ?? "";

    //handle legacy
    let legacyType = () => {
      if (url.searchParams.get("type")) {
        if (formtype === "group_enquiry" || formtype === "group_callback") {
          setProgramCode(url.searchParams.get("program"));
        }
        if (url.search.includes("flex_program")) {
          return "ingo_enquiry";
        } else if (url.search.includes("journey_program")) {
          return "journeys_enquiry";
        } else {
          return url.searchParams.get("type");
        }
      } else if (
        url.search.includes("callback") &&
        url.search.includes("program")
      ) {
        return "specific_callback";
      } else if (
        url.search.includes("callback") &&
        url.search.includes("raleigh")
      ) {
        return "callback_raleigh";
      } else if (
        url.search.includes("callback") &&
        url.search.includes("consultation")
      ) {
        return "callback_consultation";
      } else if (url.search.includes("callback")) {
        return "callback";
      } else if (
        url.search.includes("enquiry") &&
        url.search.includes("program")
      ) {
        return "specific_enquiry";
      } else if (url.searchParams.get("program") === "online") {
        setProgramCode(url.searchParams.get("program_id"));
        return "online_enquiry";
      } else if (url.searchParams.get("program") === "virtual") {
        setProgramCode(url.searchParams.get("program_id"));
        return "virtual_enquiry";
      } else if (url.searchParams.get("program") === "virtual_career") {
        setProgramCode(url.searchParams.get("program_id"));
        return "virtual_career_enquiry";
      } else if (url.searchParams.get("program") === "virtual_uncc") {
        setProgramCode(url.searchParams.get("program_id"));
        return "virtual_uncc_enquiry";
      } else if (url.search.includes("consultation")) {
        return "enquiry_consultation";
      } else if (url.search.includes("raleigh")) {
        return "enquiry_raleigh";
      } else if (url.pathname.includes("outreach")) {
        return "outreach";
      } else {
        return "enquiry";
      }
    };

    setType(legacyType() ?? "enquiry");
  }, [currentUrl]);

  useEffect(() => {
    if (programLegacyId && !programRecordTypeId) {
      //get the program title & program code and set them
      const theProgram = programs
        .filter(
          (program) =>
            parseInt(programLegacyId) === parseInt(program.databaseId)
        )
        .shift();

      setProgramTitle(theProgram?.title ?? programTitle);
      setProgramCode(theProgram?.acfProgramsMeta?.programCode ?? "");
    }
  }, [programLegacyId, programs]);

  const onSubmit = async (data) => {
    try {
      window.scrollTo(0, 0);

      const typesToFormName = {
        callback: "callback_form",
        enquiry: "enquiry_form",
        specific_enquiry: "enquiry_form",
        ingo_enquiry: "ingo_enquiry_form",
        ingo_specific_enquiry: "ingo_enquiry_form",
        journeys_enquiry: "journeys_enquiry_form",
        journeys_specific_enquiry: "journeys_enquiry_form",
        group_callback: "group_callback_form",
        group_enquiry: "group_enquiry_form",
        online_enquiry: "gvionline_enquiry_form",
        virtual_enquiry: "gvivirtual_enquiry_form",
        virtual_career_enquiry: "gvivirtual_career_enquiry_form",
        virtual_uncc_enquiry: "gvivirtual_uncc_enquiry_form",
        specific_callback: "callback_form",
        callback_raleigh: "callback_form_raleigh",
        callback_consultation: "callback_form_consultation",
        enquiry_consultation: "enquiry_form_consultation",
        enquiry_raleigh: "enquiry_form_raleigh",
        outreach: "outreach_form"
      };

      const formFields = {
        form_type: typesToFormName[type] ?? "enquiry_form",
        ...data
      };
      // console.debug("formFields => ", formFields);
      setStatus("processing");

      const prefillCalendly = {
        name: [formFields?.first_name, formFields?.last_name].join(" "),
        email: formFields?.email_address
      };

      setCalendlyPrefill(prefillCalendly);

      const formProcessResult = await processForm({
        formdata: formFields,
        peopleDomain,
        planetDomain,
        currentBrand,
        localeLanguage
      });

      if (
        (formProcessResult?.response &&
          formProcessResult?.response[0] &&
          formProcessResult?.response[0].success) ||
        (formProcessResult?.response && formProcessResult?.response.success)
      ) {
        const firebaseId =
          formProcessResult["submitted-data"]?.firebaseId ||
          formProcessResult["submitted-data"]?.firestoreId ||
          formProcessResult["submitted-data"]?.firebaseID__c ||
          formProcessResult["submitted-data"][0]?.firebaseId ||
          formProcessResult["submitted-data"][0]?.firestoreId ||
          formProcessResult["submitted-data"][0]?.firebaseID__c ||
          "";

        const { "submitted-data": submittedData } = formProcessResult || {};

        const { lead_score } = submittedData || {};

        //set enquiry data
        const forwardingStateData = {
          ...formFields,
          lead_id: formProcessResult?.response[0]
            ? formProcessResult?.response[0]?.id
            : formProcessResult?.response?.id ?? "",
          referring_form_type: formFields?.form_type,
          firebase_id: firebaseId,
          lead_score: lead_score || 0,
          program_code: programCode,
          program_title: programTitle,
          program_id: programCode ?? formFields?.program_id,
          submission_type: "enquiry"
        };

        const exclude_step_2 = new RegExp(
          step2_exclusion_list.join("|"),
          "gi"
        ).test(formFields?.form_type);

        let redirectUrl = [
          "/thank-you-for-your-application/?_",
          new Date()?.getTime()
        ]?.join("");

        if (exclude_step_2) {
          redirectUrl = [
            "/thank-you-for-your-complete-application/?_",
            new Date()?.getTime()
          ]?.join("");
        }

        setStatus("processing");
        reset();

        if (/outreach/gi.test(type)) {
          setTimeout(() => {
            navigate("/find-a-program/");
          }, 3000);
        }

        await navigate(redirectUrl, { state: forwardingStateData });
      }
    } catch (err) {
      console.error("Error requestForm :: ", err);
    }
  };

  const formPrefixes = {
    specific_enquiry: "You are enquiring about",
    specific_callback: "You are requesting a callback for",
    group_callback: "You are requesting a callback for",
    group_enquiry: "You are enquiring about",
    virtual: "You are enquiring about"
  };

  const expTexts = {
    processing: "Just a moment...",
    success: "Thank you!"
  };

  const statusTexts = {
    processing: `We're making sure we have everything we need before connecting you with one of our team members!`,
    success: `<div>One of our expert consultants will be in touch within 12 hours. If you can’t wait to speak to us, you can call us on <a class="font-bold" href="tel:${intlNumber}" title="${intlNumber}" rel="nofollow">${intlNumber}.</a></div>${type.includes("outreach") ? "<div>For now, <a href='/find-a-program/' class='font-bold'>click here</a> to find your perfect program.</div>" : ""}`
  };

  const [, setIsFocused] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const formTitle = () => {
    switch (true) {
      case type.includes("consultation"):
        return "Free consultation";

      case type.includes("group_enquiry"):
        showPromo = false;
        return "Groups enquiry form";

      case type.includes("group_callback"):
        showPromo = false;
        return "Groups call back request";

      case type.includes("enquiry"):
        return "Enquiry form";

      case type.includes("outreach"):
        showPromo = false;
        return "Participate with GVI";

      case type.includes("virtual"):
        showPromo = false;
        return "Virtual internship enquiry form";

      default:
        return "Call back request";
    }
  };

  const travelToggle = (event) => {
    const selVal = event.target.value;

    if (selVal.toLowerCase() === "yes" && event.target.checked) {
      setValue("travel_next_year", "book_next_year");
      setValue("travel_no", "");
      setValue("travel_yes", selVal);
    } else if (selVal.toLowerCase() === "no" && event.target.checked) {
      setValue("travel_next_year", "no_book_next_year");
      setValue("travel_yes", "");
      setValue("travel_no", selVal);
    } else {
      setValue("travel_no", "");
      setValue("travel_yes", "");
      setValue("travel_next_year", "");
    }
  };

  const customStyles = formDropdownPrimaryStyle(errors);

  const todaysDate = new Date();
  const currentYear = todaysDate.getUTCFullYear();
  const startCountYear = parseInt(currentYear - 100);
  const dateRange = arrayRange(startCountYear, currentYear, 1);

  const yearOfBirthOptions = dateRange?.map((item) => ({
    value: item,
    label: item
  }));

  const currencyToUse = currentSiteMeta?.currency
    ? currentSiteMeta.currency.toLowerCase()
    : process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const currentMaxPriceFilter = budgetPricing?.budgetPricing?.find(
    (filter) => filter?.budgetPricingIsoCode?.toLowerCase() === currencyToUse
  );

  const formatLabelWithCurrency = (label, currency) => {
    return label.replace(/\d+([,-]\d+)?/g, (match) =>
      formatCurrencyAmount({ currentSiteMeta, amount: match, currency })
    );
  };

  const minPriceFilterAmount =
    currentMaxPriceFilter?.budgetPricingOptions?.map((item) => {
      const label = formatLabelWithCurrency(
        item?.budgetPricingOptionsLabel,
        currentSiteMeta?.currency_symbol ??
          process.env.GATSBY_PROJECT_CURRENCY ??
          ""
      );

      return {
        value: item?.budgetPricingOptionsLabel,
        label: label
      };
    }) ?? "";

  const ingoProgram = programTitle?.includes("INGO");

  const programLocations = [];

  if (type.includes("outreach") && programs) {
    // eslint-disable-next-line array-callback-return
    programs?.map((program) => {
      const locations = flattenMenu(program?.geographical?.nodes, {
        idKey: "id",
        parentKey: "parentId",
        childrenKey: "children"
      });

      const desktopLocation = locations?.flatMap((loc) => loc.children);

      desktopLocation.forEach((location) => {
        programLocations[location.slug] = {
          label: location.name,
          value: location.slug
        };
      });
    });
  }

  const locationOptions = Object.values(programLocations).sort(sortBy("value"));

  return (
    <>
      {backLocalImg &&
        backImage &&
        backImage?.localFile?.extension &&
        backImage?.localFile?.extension !== "svg" && (
          <Image
            imageAlt=""
            image={backLocalImg}
            imageClass="absolute z-0 object-cover w-full h-full"
            sourceUrl=""
          />
        )}

      {!backLocalImg && (
        <Image
          imageAlt=""
          image={""}
          imageClass="absolute top-0 bottom-0 left-0 right-0 z-0 object-cover w-full h-full"
          sourceUrl={mediaItemUrl}
        />
      )}

      <div className="sm:container z-10 relative flex flex-col justify-between w-full sm:w-full gvi-blur-bg gvi-border-05 rounded-br-xxs rounded-bl-xxs border-white py-12 px-5 xs:py-12 xs:px-6 sm:mx-auto my-0 xs:mt-20 xs:mb-32">
        {status === "pending" ? (
          <FormProvider {...form}>
            <form
              id={"requestForm"}
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col items-center justify-between w-11/12 mx-auto sm:w-full sm:px-16"
            >
              <div className="relative flex flex-col items-center w-full pb-5 sm:items-start">
                <h2
                  className={`text-white font-body font-semibold leading-none
                 ${programTitle ? "pb-3" : "pb-6"}`}
                  dangerouslySetInnerHTML={{ __html: formTitle() }}
                />

                {programTitle && (
                  <>
                    <h3 className="w-full font-light text-center text-white font-body sm:text-left sm:w-1/2">
                      {formPrefixes[type] ?? ""}
                    </h3>
                    <h3 className="pb-4 font-semibold text-center text-white font-body sm:text-left sm:pb-0">
                      {programTitle ?? ""}
                    </h3>
                  </>
                )}

                {showPromo && (
                  <div
                    className="flex relative sm:absolute right-0 px-6 sm:px-4 py-3 sm:py-2 w-full sm:w-5/12 lg:w-64
              border-0 sm:border border-gray-testimonial rounded-full bg-gray-darken/30 items-center"
                  >
                    <div className="pl-2 text-base font-semibold text-white text-center w-full">
                      Grants worth up to 80% <br />
                      <span className="font-normal">
                        of program fees available
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col w-full sm:flex-row">
                <label className="w-full pb-3 sm:w-1/2 sm:pb-0 sm:pr-2 relative">
                  <input
                    type="text"
                    placeholder="First Name*"
                    className={`
                  w-full bg-transparent gvi-border-light gvi-border-05 px-3 pr-16 md:pr-14 py-4 sm:py-3 rounded-sm text-white focus:text-white focus:outline-none placeholder:text-white placeholder:font-bold planet:focus:text-white planet:text-white planet:font-bold${
                    errors.first_name ? " !border-red-600 " : ""
                  }
                `}
                    {...register("first_name", {
                      required: true,
                      pattern: /^[^\s].*/
                    })}
                  />{" "}
                  <div
                    className={`tooltip tooltip-left w-1/6 md:w-5 !absolute py-3 mx-0 md:mx-4 right-0 top-1 md:right-5 before:py-3 before:rounded-xxs before:bg-white before:planet:bg-gray-standard before:text-gray-standard before:planet:text-white tooltip-${tooltipOpen}`}
                    data-tip="This is how we will refer to you."
                  >
                    <InfoIcon
                      onClick={() => setTooltipOpen(1 - tooltipOpen)}
                      className="fill-white stroke-transparent group-hover:sm:fill-white mx-2 z-10"
                    />
                  </div>
                </label>
                <label className="w-full sm:w-1/2 sm:pl-1">
                  <input
                    type="text"
                    placeholder="Last Name*"
                    className={`w-full bg-transparent gvi-border-light gvi-border-05 px-3 py-4 sm:py-3 rounded-sm text-white focus:outline-none focus:text-white placeholder:text-white placeholder:font-bold planet:focus:text-white planet:text-white planet:font-bold${
                      errors.last_name ? " !border-red-600 " : ""
                    }`}
                    {...register("last_name", {
                      required: true,
                      pattern: /^[^\s].*/
                    })}
                  />
                </label>
              </div>

              <div className="flex flex-col w-full py-3 sm:flex-row sm:py-4">
                <label className="w-full pb-3 sm:w-1/2 sm:pb-0 sm:pr-2">
                  <input
                    type="number"
                    placeholder="Phone*"
                    className={`w-full bg-transparent gvi-border-light gvi-border-05  px-3 py-4 sm:py-3 rounded-sm text-white focus:outline-none focus:text-white placeholder:text-white placeholder:font-bold planet:focus:text-white planet:text-white planet:font-bold${
                      errors.phone_number ? " !border-red-600 " : ""
                    }`}
                    {...register("phone_number", {
                      pattern: /\d+/,
                      required: true
                    })}
                  />
                </label>
                <label className="w-full sm:w-1/2 sm:pl-1">
                  <input
                    type="text"
                    placeholder="Email*"
                    className={`w-full bg-transparent gvi-border-light gvi-border-05  px-3 py-4 sm:py-3 rounded-sm text-white focus:outline-none focus:text-white placeholder:text-white placeholder:font-bold planet:focus:text-white planet:text-white planet:font-bold ${
                      errors.email_address ? " !border-red-600 " : ""
                    }`}
                    {...register("email_address", {
                      required: true,
                      pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                    })}
                  />
                </label>
              </div>

              {minPriceFilterAmount?.length > 0 && (
                <div className="flex flex-col w-full py-3 sm:flex-row sm:py-0">
                  <label className="w-full pb-3 sm:w-1/2 sm:pb-0 sm:pr-2">
                    <FormInputSelect
                      name="program_spend"
                      options={minPriceFilterAmount}
                      handleChange={""}
                      styles={customStyles}
                      setValue={setValue}
                      rules={{ required: true }}
                      placeholder="What is your budget?*"
                      className={`w-full h-full bg-transparent text-gray-lighter`}
                    />
                  </label>
                </div>
              )}

              {programTitle && type.includes("enquiry") && !ingoProgram && (
                <label className="w-full mt-6 pb-1 flex items-center text-white custom-checkbox">
                  <input
                    type="checkbox"
                    className="absolute opacity-0"
                    value={"true"}
                    {...register("under18", {})}
                    onClick={(e) => {
                      setChecked(e.target.checked);
                    }}
                  />
                  <span className="checkmark sm:!top-1  w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                  Please tick if you are looking for an under 18 project.
                </label>
              )}

              {type.includes("outreach") ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 w-full gap-4 pb-8">
                  <div className="col-span-full">
                    <input
                      type="text"
                      placeholder="What are you currently studying?*"
                      className={`w-full bg-transparent gvi-border-light gvi-border-05 px-3 py-4 sm:py-3 rounded-sm text-white focus:outline-none focus:text-white placeholder:text-white placeholder:font-bold planet:focus:text-white planet:text-white planet:font-bold${
                        errors.field_of_study ? " !border-red-600 " : ""
                      }`}
                      {...register("field_of_study", {
                        required: true,
                        maxLength: {
                          value: 50,
                          message:
                            "The field of study cannot be longer than 50 characters"
                        }
                      })}
                    />
                    {errors.field_of_study && (
                      <p className="text-red-600">
                        {errors.field_of_study.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="text-white font-bold">Program Interest</div>

                    <FormInputSelect
                      name="interested_in_focus_type"
                      options={programInterests}
                      handleChange={""}
                      styles={customStyles}
                      setValue={setValue}
                      placeholder="Please choose one*"
                      className={`w-full h-full bg-transparent select-in-step select-menu-app-form text-left text-gray-lighter ${
                        errors.interested_in_focus_type
                          ? " !border-red-600 "
                          : ""
                      }`}
                      rules={{ required: true }}
                    />
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="text-white font-bold">Year of birth</div>

                    <FormInputSelect
                      name="dob_y"
                      options={yearOfBirthOptions?.reverse()}
                      handleChange={""}
                      styles={customStyles}
                      setValue={setValue}
                      placeholder="Year*"
                      className={`w-full h-full bg-transparent select-in-step select-menu-app-form text-left text-gray-lighter ${
                        errors.dob_y ? " !border-red-600 " : ""
                      }`}
                      rules={{ required: true }}
                    />
                  </div>
                  <div className="col-span-full">
                    <div className="flex flex-col gap-3">
                      <div className="text-white font-bold">
                        Preferred Destination
                      </div>

                      <FormInputSelect
                        name="country_preference"
                        options={locationOptions}
                        handleChange={""}
                        styles={customStyles}
                        setValue={setValue}
                        placeholder="Please choose one"
                        className={`w-full md:w-1/2 pr-3 py-2 h-full bg-transparent select-in-step select-menu-app-form text-left text-gray-lighter ${
                          errors.country_preference ? " !border-red-600 " : ""
                        }`}
                        rules={{ required: true }}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="text-white font-bold">
                      I would like to speak to someone from GVI*
                    </div>
                    <label
                      htmlFor="speak_to_sales_consultant"
                      className="relative text-left flex items-center text-white text-base"
                    >
                      <div className={`flex gap-3 items-center `}>
                        <input
                          type="radio"
                          {...register("speak_to_sales_consultant", {
                            required: true
                          })}
                          id="speak_to_sales_consultant"
                          value={"Yes"}
                          className="absolute opacity-0"
                          // onChange={(e) => speakToSomeoneToggle(e)}
                        />
                        <div
                          className={`w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex flex-shrink-0 justify-center items-center ${
                            errors.speak_to_sales_consultant
                              ? "!border-red-600"
                              : ""
                          }`}
                        >
                          <Tick />
                        </div>
                        <div>Yes</div>
                      </div>
                    </label>

                    <label
                      htmlFor="speak_no"
                      className="relative text-left flex items-center text-white text-base"
                    >
                      <div className={`flex gap-3 items-center `}>
                        <input
                          type="radio"
                          {...register("speak_to_sales_consultant", {
                            required: true
                          })}
                          id="speak_to_sales_consultant"
                          value={"No"}
                          className="absolute opacity-0"
                          // onChange={(e) => speakToSomeoneToggle(e)}
                        />
                        <div
                          className={`w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex flex-shrink-0 justify-center items-center ${
                            errors.speak_to_sales_consultant
                              ? "!border-red-600"
                              : ""
                          }`}
                        >
                          <Tick />
                        </div>
                        <div>No</div>
                      </div>
                    </label>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="text-white font-bold">
                      Would you like to travel in the next year?
                    </div>
                    <label
                      htmlFor="travel_yes"
                      className="relative text-left flex items-center text-white  text-base "
                    >
                      <div className="flex gap-3 items-center">
                        <input
                          type="checkbox"
                          {...register("travel_yes")}
                          id="travel_yes"
                          value={"Yes"}
                          className="absolute opacity-0"
                          onChange={(e) => travelToggle(e)}
                        />
                        <div
                          className={
                            "w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex flex-shrink-0 justify-center items-center "
                          }
                        >
                          <Tick />
                        </div>
                        <div>Yes</div>
                      </div>
                    </label>

                    <label
                      htmlFor="travel_no"
                      className="relative text-left flex items-center text-white  text-base "
                    >
                      <div className="flex gap-3 items-center">
                        <input
                          type="checkbox"
                          {...register("travel_no")}
                          id="travel_no"
                          value={"No"}
                          className="absolute opacity-0"
                          onChange={(e) => travelToggle(e)}
                        />
                        <div
                          className={
                            "w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex flex-shrink-0 justify-center items-center "
                          }
                        >
                          <Tick />
                        </div>

                        <div>No</div>
                      </div>
                    </label>
                  </div>
                  <div className="flex flex-col w-full pt-3 pb-6">
                    <label
                      htmlFor="program_conditions"
                      className="text-base font-bold pb-2 gap-3 flex items-center !text-white"
                    >
                      <input
                        type="checkbox"
                        className="absolute opacity-0"
                        id="program_conditions"
                        value={"program_conditions"}
                        {...register("program_conditions", { required: true })}
                      />
                      <div
                        className={`w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex flex-shrink-0 justify-center items-center ${
                          errors.program_conditions ? "!border-red-600" : ""
                        }`}
                      >
                        <Tick />
                      </div>
                      I am aware that GVI programs come at a cost.*
                    </label>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col w-full pt-3 pb-6">
                  <p className="pb-2 text-white">
                    Do you have any questions or comments?
                  </p>
                  <textarea
                    className="text-white bg-transparent form-control gvi-border-light gvi-border-05 focus:outline-none focus:text-white py-2 px-4"
                    rows="3"
                    {...register("why_want_to_apply", {})}
                  />
                </div>
              )}

              <div className="flex flex-col justify-center w-full pb-8">
                {!type.includes("outreach") && (
                  <>
                    {currencyToUse === "usd" && (
                      <label
                        className={`${currencyToUse} font-semi-bold h8 pb-2 pt-1 flex !text-white custom-checkbox`}
                      >
                        <input
                          type="checkbox"
                          className="absolute opacity-0"
                          value={"sms_consent"}
                          {...register("sms_consent", {})}
                        />
                        <span className="checkmark sm:!top-1  w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                        I would like to receive texts from GVI, including
                        program info and easy appointment booking links.
                      </label>
                    )}
                    <label className="font-semi-bold h8 pb-2 pt-1 flex !text-white custom-checkbox">
                      <input
                        type="checkbox"
                        className="absolute opacity-0"
                        value={"newsletter_signup"}
                        {...register("newsletter", {})}
                      />
                      <span className="checkmark sm:!top-1  w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                      I would like to sign up to the GVI newsletter to receive
                      updates, news and special offers.
                    </label>
                  </>
                )}
                <p className="font-semi-bold h8 !text-white py-2">
                  By clicking &quot;
                  {type.includes("consultation")
                    ? "Contact me"
                    : type.includes("enquiry")
                      ? "Enquire"
                      : type.includes("outreach")
                        ? "Submit"
                        : "Send request"}
                  &quot; below, I have read and accepted GVI&apos;s{" "}
                  <a
                    className="text-white border-b border-white planet:sm:border-white font-semi-bold planet:border-b-white hover:border-b-yellow-standard planet:hover:border-green-standard hover:text-yellow-standard planet:hover:!text-green-standard active:border-b-yellow-standard planet:active:border-green-standard active:text-yellow-standard planet:active:!text-green-standard"
                    href="/privacy"
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>

              <div className="hidden hidden-fields">
                {programCode && (
                  <input
                    type="hidden"
                    defaultValue={programCode}
                    {...register("program_id")}
                  />
                )}
                {programRecordTypeId && (
                  <input
                    type="hidden"
                    defaultValue={programRecordTypeId}
                    {...register("record_type_id")}
                  />
                )}
                <input
                  type="hidden"
                  defaultValue="privacy_policy"
                  {...register("privacy_policy")}
                />
                {type.includes("outreach") && eventId && (
                  <input
                    type="hidden"
                    defaultValue={eventId}
                    {...register("event_id")}
                  />
                )}
              </div>

              <div className="flex justify-end w-full button-submit">
                <button
                  type="submit"
                  className="rounded-full planet:rounded-sm p-2 flex justify-center items-center w-full sm:w-1/3 mx-auto !text-base !font-bold bg-red-light planet:bg-green-standard !text-white planet:!text-gray-darker"
                >
                  {type.includes("consultation")
                    ? "Contact me"
                    : type.includes("enquiry")
                      ? "Enquire"
                      : type.includes("outreach")
                        ? "Submit"
                        : "Send request"}
                  {/* <ArrowRight className="w-3 h-3 ml-3 stroke-white planet:stroke-gray-darker" /> */}
                </button>
              </div>
            </form>
          </FormProvider>
        ) : (
          <StatusComp
            expText={expTexts[status]}
            statusText={statusTexts[status]}
            status={status}
            isGold={!!programLegacyId}
            calendlyActive={speakToConsultant}
            calendlyPrefill={calendlyPrefill}
          />
        )}
      </div>
    </>
  );
};
RequestForm.defaultProps = {};

export default RequestForm;
